import React from "react";
import Zoom from "@mui/material/Zoom";
import { Box, Tooltip } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Alert02Icon,
  CloudOffIcon,
  CloudSavingDone01Icon,
  CloudUploadIcon,
} from "@hugeicons-pro/core-stroke-standard";
import { useTranslation } from "react-i18next";
import { Chip } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";

interface RenderSyncStatusProps {
  isIntegrationSynced?: boolean | null;
  /** Pass false for a row whose type or status means it can never reach the ledger. */
  isSyncEligible?: boolean;
  /** Why the last attempt failed; shown until an attempt succeeds. */
  syncError?: string | null;
}

type SyncState = "synced" | "failed" | "notSynced" | "notEligible";

// "default" is the chip's quietest tone, for not applicable, which most rows have.
const SYNC_STATES: Record<
  SyncState,
  {
    icon: typeof CloudSavingDone01Icon;
    tone: ChipTone;
    name: string;
    tooltip: string;
  }
> = {
  synced: {
    icon: CloudSavingDone01Icon,
    tone: "positive",
    name: "Synced",
    tooltip: "SyncedTooltip",
  },
  failed: {
    icon: Alert02Icon,
    tone: "negative",
    name: "SyncError",
    tooltip: "SyncErrorTooltip",
  },
  notSynced: {
    icon: CloudUploadIcon,
    tone: "neutral",
    name: "NotSynced",
    tooltip: "NotSyncedTooltip",
  },
  notEligible: {
    icon: CloudOffIcon,
    tone: "default",
    name: "NotApplicable",
    tooltip: "SyncNotApplicableTooltip",
  },
};

const syncStateOf = ({
  isIntegrationSynced,
  isSyncEligible,
  syncError,
}: RenderSyncStatusProps): SyncState => {
  // The last attempt decides: a failure after an earlier success still needs attention.
  if (syncError) return "failed";
  if (isIntegrationSynced) return "synced";
  return isSyncEligible ? "notSynced" : "notEligible";
};

export const RenderSyncStatus: React.FC<RenderSyncStatusProps> = (props) => {
  const { t } = useTranslation();
  const { syncError, isSyncEligible = true } = props;
  const state = syncStateOf({ ...props, isSyncEligible });
  const { icon, tone, name, tooltip } = SYNC_STATES[state];
  const detail = state === "failed" && syncError ? `: ${syncError}` : "";
  const title = `${t(name)}: ${t(tooltip)}${detail}`;

  return (
    <Tooltip
      title={title}
      describeChild
      disableInteractive
      slots={{
        transition: Zoom,
      }}
    >
      <Box
        component="span"
        tabIndex={0}
        role="img"
        aria-label={t(name)}
        sx={{ display: "inline-flex" }}
      >
        <Chip
          size="sm"
          tone={tone}
          leading={<HugeiconsIcon icon={icon} size={14} />}
        />
      </Box>
    </Tooltip>
  );
};

export default RenderSyncStatus;
